/** Client management (B01-001..005): list, add + invite, pause/resume, detail blade, per-client audit log. */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { Icon } from '../../../shell/Icon';
import { CLIENT_DETAIL_TABS } from '@qm/contracts/workspace';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Blade, DataTable, ErrorNote, Field, Loadable, Notice, ReasonForm, Screen, Tabs, When, type Column } from '../kit/ui';
import type { AuditEntry, ClientDetail, ClientView, WaNumberView } from '../api';
import { CLIENT_LIST_TABS, clientSummary, filterAudit, isAllowanceExhausted } from '../lib/logic';
import { avatarTone, clientFilterAvailability, clientOwnerOptions, clientPlanOptions, clientServiceOptions, deliverableRatio, filterClients, initials, maskPhone, phoneByClient } from '../lib/views';
import { usePartnerApi } from './layout';
import { ClientYouTube } from './youtube';

const WA_FILTERS = ['connected', 'disconnected', 'not_connected', 'error'] as const;

export function ClientsPage() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const nav = useNavigate();
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const tab = (CLIENT_LIST_TABS as readonly string[]).includes(sp.get('status') ?? '') ? sp.get('status')! : 'all';
  const health = sp.get('health') ?? '';
  const wa = (WA_FILTERS as readonly string[]).includes(sp.get('wa') ?? '') ? sp.get('wa')! : '';
  const q = sp.get('q') ?? '';
  const plan = sp.get('plan') ?? '';
  const owner = sp.get('owner') ?? '';
  const service = sp.get('service') ?? '';
  const query = { tab, ...(health ? { health } : {}), ...(wa ? { wa } : {}), ...(q ? { q } : {}) };
  const key = `clients:${JSON.stringify(query)}`;
  const list = useResource(key, () => api.clients(query as never));
  const numbers = useResource('wa:numbers', api.waNumbers);
  const phones = numbers.status === 'ready' ? phoneByClient(numbers.data) : {};
  const setParam = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); };

  const cols: Column<ClientView>[] = [
    {
      key: 'name', header: t('portal.partners.clients.col.name'), render: (c) => (
        <Link className="qp-name" to={`/clients/${encodeURIComponent(c.id)}?tab=summary`}>
          <span className="qs-avatar" data-tone={avatarTone(c.name.length)} aria-hidden="true">{initials(c.name)}</span>
          <span>{c.name}</span>
        </Link>
      ),
    },
    {
      key: 'health', header: t('portal.partners.clients.col.health'), render: (c) => {
        const s = clientSummary(c);
        if (s.onboarding) return <Badge testId="health-onboarding">{t('portal.partners.clients.onboarding')}</Badge>;
        return (
          <span>
            <span className="qp-health" data-testid={`health-${s.band}`}><span className="qs-dot" data-tone={HEALTH_DOT[s.band!]} aria-hidden="true" /><span>{t(`portal.partners.health.${s.band}`)} {s.score}</span></span>
            {s.reasons.length ? <span className="qx-field__hint" data-testid="health-reasons"> {s.reasons.map((r) => t(`workspace.health.${r}`)).join(', ')}</span> : null}
          </span>
        );
      },
    },
    { key: 'leads', header: t('portal.partners.clients.col.leads'), render: (c) => (clientSummary(c).onboarding ? '-' : c.leads ?? 0) },
    { key: 'deliverables', header: t('portal.partners.clients.col.deliverables'), narrow: 'hide', render: (c) => (clientSummary(c).onboarding ? '-' : deliverableRatio(c) ?? '-') },
    { key: 'report', header: t('portal.partners.clients.col.nextReport'), narrow: 'hide', render: (c) => <When iso={c.nextReportAt} /> },
    {
      key: 'wa', header: t('portal.partners.clients.col.whatsapp'), render: (c) => (c.whatsapp ? (
        <span className="qp-wa">
          <span className="qs-status"><span className="qs-dot" data-tone={c.whatsapp === 'connected' ? 'good' : c.whatsapp === 'not_connected' ? undefined : 'bad'} aria-hidden="true" />{t(`portal.partners.wa.${c.whatsapp}`)}</span>
          {phones[c.id] ? <bdi data-testid="wa-masked">{maskPhone(phones[c.id])}</bdi> : null}
        </span>
      ) : '-'),
    },
    { key: 'activity', header: t('portal.partners.clients.col.lastActivity'), narrow: 'hide', render: (c) => (c.lastActivityAt ? <When iso={c.lastActivityAt} /> : '-') },
    { key: 'status', header: t('portal.partners.clients.col.status'), narrow: 'hide', render: (c) => <Badge>{t(`portal.partners.clientStatus.${c.status}`)}</Badge> },
  ];

  return (
    <Screen title={t('portal.partners.clients.title')} actions={<Link to="/clients/new" className="qm-button" data-variant="primary">{t('portal.partners.clients.add')}</Link>} testId="clients">
      <div className="qp-clients-split" data-open={id ? 'true' : undefined}>
        <div className="qp-clients">
          <div className="qs-filters">
            <Field label={t('portal.partners.clients.search')}>
              {(fid) => <input id={fid} type="search" name="q" key={q} defaultValue={q} onKeyDown={(e) => { if (e.key === 'Enter') setParam('q', (e.target as HTMLInputElement).value); }} />}
            </Field>
            <Field label={t('portal.partners.clients.filterHealth')}>
              {(fid) => (
                <select id={fid} name="health" value={health} onChange={(e) => setParam('health', e.target.value)}>
                  <option value="">{t('portal.partners.clients.any')}</option>
                  {(['healthy', 'watch', 'at_risk'] as const).map((b) => <option key={b} value={b}>{t(`portal.partners.health.${b}`)}</option>)}
                </select>
              )}
            </Field>
            <Field label={t('portal.partners.clients.filterWhatsapp')}>
              {(fid) => (
                <select id={fid} name="wa" value={wa} onChange={(e) => setParam('wa', e.target.value)}>
                  <option value="">{t('portal.partners.clients.any')}</option>
                  {WA_FILTERS.map((w) => <option key={w} value={w}>{t(`portal.partners.wa.${w}`)}</option>)}
                </select>
              )}
            </Field>
            {(() => {
              const rowsAll = list.status === 'ready' ? list.data.data : [];
              const av = clientFilterAvailability(rowsAll);
              const need = (k: 'plan' | 'owner' | 'service') => (av[k] ? undefined : t('portal.partners.clients.needs', { field: t(`portal.partners.clients.field.${k}`) }));
              return (
                <>
                  <Field label={t('portal.partners.clients.filterPlan')} hint={need('plan')}>
                    {(fid, d) => (
                      <select id={fid} name="plan" value={plan} disabled={!av.plan} aria-describedby={d} onChange={(e) => setParam('plan', e.target.value)}>
                        <option value="">{t('portal.partners.clients.any')}</option>
                        {clientPlanOptions(rowsAll).map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    )}
                  </Field>
                  <Field label={t('portal.partners.clients.filterOwner')} hint={need('owner')}>
                    {(fid, d) => (
                      <select id={fid} name="owner" value={owner} disabled={!av.owner} aria-describedby={d} onChange={(e) => setParam('owner', e.target.value)}>
                        <option value="">{t('portal.partners.clients.any')}</option>
                        {clientOwnerOptions(rowsAll).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                    )}
                  </Field>
                  <Field label={t('portal.partners.clients.filterService')} hint={need('service')}>
                    {(fid, d) => (
                      <select id={fid} name="service" value={service} disabled={!av.service} aria-describedby={d} onChange={(e) => setParam('service', e.target.value)}>
                        <option value="">{t('portal.partners.clients.any')}</option>
                        {clientServiceOptions(rowsAll).map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    )}
                  </Field>
                </>
              );
            })()}
            <Button type="button" data-testid="clients-clear" onClick={() => setSp({})}>{t('portal.partners.clients.clear')}</Button>
          </div>
          <Loadable resource={list}>
            {(env) => (
              <>
                <Tabs label={t('portal.partners.clients.tabs')} active={tab} onSelect={(v) => setParam('status', v === 'all' ? '' : v)}
                  tabs={CLIENT_LIST_TABS.map((s) => ({ id: s, label: t(`portal.partners.clientStatus.${s}`), count: (env.meta?.tabs as Record<string, number> | undefined)?.[s] ?? null }))} />
                <DataTable columns={cols} rows={filterClients(env.data, { plan, owner, service })} rowKey={(c) => c.id} empty={t('portal.partners.clients.empty')} selectedKey={id ?? null} onRowClick={(c) => nav(`/clients/${encodeURIComponent(c.id)}?tab=summary`)} />
              </>
            )}
          </Loadable>
        </div>
        {id ? <ClientBlade id={id} /> : null}
      </div>
    </Screen>
  );
}

const HEALTH_DOT = { healthy: 'good', watch: 'warn', at_risk: 'bad' } as const;

export function ClientBlade({ id }: { id: string }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = (CLIENT_DETAIL_TABS as readonly string[]).includes(sp.get('tab') ?? '') ? sp.get('tab')! : 'summary';
  const detail = useResource(`client:${id}:${tab}`, () => api.client(id, tab));
  const numbers = useResource<WaNumberView[]>('wa:numbers', api.waNumbers);
  const phone = numbers.status === 'ready' ? phoneByClient(numbers.data)[id] : undefined;
  const open = (v: string) => { const n = new URLSearchParams(sp); n.set('tab', v); setSp(n); };
  return (
    <Blade docked title={detail.status === 'ready' ? detail.data.client.name : t('portal.partners.clients.detail')} onClose={() => nav('/clients')} testId="client-blade">
      <Tabs label={t('portal.partners.clients.detail')} active={tab} onSelect={open}
        tabs={CLIENT_DETAIL_TABS.map((x) => ({ id: x, label: t(`portal.partners.clientTab.${x}`) }))} />
      <Loadable resource={detail}>{(d) => <ClientTab detail={d} phone={phone} onOpenTab={open} onChanged={detail.reload} />}</Loadable>
    </Blade>
  );
}

function ClientTab({ detail, phone, onOpenTab, onChanged }: { detail: ClientDetail; phone?: string; onOpenTab: (tab: string) => void; onChanged: () => void }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const c = detail.client;
  const pause = useAction((reason: string) => api.pauseClient(c.id, reason));
  const resume = useAction(() => api.resumeClient(c.id));
  if (detail.tab === 'summary') {
    const s = clientSummary(c);
    return (
      <div className="qp-detail" data-testid="tab-summary">
        <div className="qp-detail__top">
          <span className="qs-avatar" data-tone={avatarTone(c.name.length)} aria-hidden="true">{initials(c.name)}</span>
          <div><Text>{t(`portal.partners.clientStatus.${c.status}`)}</Text></div>
        </div>
        {s.onboarding ? <Notice>{t('portal.partners.clients.onboardingNote')}</Notice> : <Text>{t('portal.partners.clients.col.health')}: {s.score}</Text>}
        <Heading level={3}>{t('portal.partners.clients.preview')}</Heading>
        <dl className="qp-preview" data-testid="client-preview">
          <div><dt>{t('portal.partners.clients.healthScore')}</dt><dd>{s.score ?? '-'}</dd></div>
          <div><dt>{t('portal.partners.clients.col.leads')}</dt><dd>{s.onboarding ? '-' : c.leads ?? 0}</dd></div>
          <div><dt>{t('portal.partners.clients.col.deliverables')}</dt><dd>{deliverableRatio(c) ?? '-'}</dd></div>
          <div><dt>{t('portal.partners.clients.col.whatsapp')}</dt><dd>{c.whatsapp ? t(`portal.partners.wa.${c.whatsapp}`) : '-'}{phone ? <> <bdi>{maskPhone(phone)}</bdi></> : null}</dd></div>
        </dl>
        {c.status !== 'archived' ? <ClientYouTube tenantId={c.id} /> : null}
        <div className="qp-note" data-testid="client-isolated"><Icon name="shield" size={18} /><div><b>{t('portal.partners.clients.isolated')}</b><p>{t('portal.partners.clients.isolatedNote')}</p></div></div>
        <div className="qp-actions">
          <Link to={`/clients/${encodeURIComponent(c.id)}/performance`} className="qm-button" data-variant="primary" data-testid="client-performance-link">{t('portal.partners.clients.openWorkspace')}</Link>
          <Link to={`/clients/${encodeURIComponent(c.id)}/analytics`} className="qm-button" data-testid="client-analytics-link">{t('connectors.ga4.nav')}</Link>
          <Button onClick={() => onOpenTab('access')}>{t('portal.partners.clients.manageAccess')}</Button>
          <Button onClick={() => onOpenTab('audit')}>{t('portal.partners.clients.viewAudit')}</Button>
        </div>
        {c.status === 'paused' ? (
          <Panel>
            <Text>{t('portal.partners.clients.pausedNote')}</Text>
            <Button variant="primary" disabled={resume.pending} onClick={async () => { if (await resume.run()) onChanged(); }}>{t('portal.partners.clients.resume')}</Button>
            {resume.result ? <Notice testId="resume-review">{t('portal.partners.clients.resumeReview', { n: resume.result.heldForReview ?? 0 })}</Notice> : null}
            <ErrorNote error={resume.error} />
          </Panel>
        ) : c.status !== 'archived' ? (
          <Panel>
            <Text tone="muted">{t('portal.partners.clients.pauseHelp')}</Text>
            <ReasonForm testId="pause-form" label={t('portal.kit.reason')} submitLabel={t('portal.partners.clients.pause')} danger pending={pause.pending} onSubmit={async (r) => { if (await pause.run(r)) onChanged(); }} />
            <ErrorNote error={pause.error} />
          </Panel>
        ) : null}
      </div>
    );
  }
  if (detail.tab === 'people') {
    const people = (detail.content as { id: string; email: string; role: string; status: string }[]) ?? [];
    return <DataTable rows={people} rowKey={(p) => p.id} empty={t('portal.kit.empty')} columns={[
      { key: 'email', header: t('portal.partners.clients.email'), render: (p) => p.email },
      { key: 'role', header: t('portal.partners.clients.role'), render: (p) => p.role },
      { key: 'status', header: t('portal.partners.clients.col.status'), render: (p) => p.status },
    ]} />;
  }
  if (detail.tab === 'services') {
    const subs = (detail.content as { plan: string; status: string; currentPeriodEnd: string | null }[]) ?? [];
    return <DataTable rows={subs} rowKey={(s) => `${s.plan}-${s.status}`} empty={t('portal.kit.empty')} columns={[
      { key: 'plan', header: t('portal.partners.clients.plan'), render: (s) => s.plan },
      { key: 'status', header: t('portal.partners.clients.col.status'), render: (s) => s.status },
      { key: 'end', header: t('portal.partners.clients.periodEnd'), render: (s) => <When iso={s.currentPeriodEnd} /> },
    ]} />;
  }
  if (detail.tab === 'access') {
    const a = detail.content as { scopes: string[]; assignedStaff: { userId: string; email: string; agencyRole: string }[] };
    return (
      <div data-testid="tab-access">
        <Text>{t('portal.partners.clients.grantedScopes')}: {a.scopes.map((s) => t(`partners.grants.scope.${s.replace(':', '_')}`)).join(', ')}</Text>
        <DataTable rows={a.assignedStaff} rowKey={(s) => s.userId} empty={t('portal.partners.clients.noStaff')} columns={[
          { key: 'email', header: t('portal.partners.clients.email'), render: (s) => s.email },
          { key: 'role', header: t('portal.partners.clients.role'), render: (s) => t(`portal.partners.agencyRole.${s.agencyRole}`) },
        ]} />
      </div>
    );
  }
  if (detail.tab === 'whatsapp') {
    const w = detail.content as { status: string };
    return <div className="qp-wa"><Badge tone={w.status === 'connected' ? 'good' : 'neutral'} testId="tab-whatsapp">{t(`portal.partners.wa.${w.status}`)}</Badge>{phone ? <bdi data-testid="wa-masked">{maskPhone(phone)}</bdi> : null}</div>;
  }
  return <ClientAudit entries={(detail.content as { entries: AuditEntry[] }).entries ?? []} />;
}

/** Append-only audit view: filter by actor, no edit or delete controls exist (B01-005). */
export function ClientAudit({ entries }: { entries: AuditEntry[] }) {
  const { t } = useSurface();
  const [actor, setActor] = useState('');
  const rows = filterAudit(entries, actor);
  return (
    <div data-testid="tab-audit">
      <Field label={t('portal.partners.audit.filterActor')}>{(id) => <input id={id} name="actor" value={actor} onChange={(e) => setActor(e.target.value)} />}</Field>
      <Text tone="muted">{t('portal.partners.audit.immutable')}</Text>
      <DataTable rows={rows} rowKey={(e) => e.id} empty={t('portal.kit.empty')} columns={[
        { key: 'actor', header: t('portal.partners.audit.actor'), render: (e) => e.actorLabel ?? e.actorId },
        { key: 'action', header: t('portal.partners.audit.action'), render: (e) => e.action },
        { key: 'reason', header: t('portal.kit.reason'), render: (e) => e.reason ?? '-' },
        { key: 'at', header: t('portal.partners.audit.time'), render: (e) => <When iso={e.at} /> },
      ]} />
    </div>
  );
}

export function NewClientPage() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [locale, setLocale] = useState<'en' | 'ar'>('en');
  const act = useAction(api.createClient);
  const submit = (e: FormEvent) => { e.preventDefault(); void act.run({ name: name.trim(), ownerEmail: ownerEmail.trim(), locale }); };
  return (
    <Screen title={t('portal.partners.clients.add')} testId="client-new">
      {act.result ? (
        <Notice tone="good" testId="client-created">{t('portal.partners.clients.created', { name: act.result.client.name, email: act.result.invite.email })}</Notice>
      ) : (
        <form onSubmit={submit}>
          <Field label={t('portal.partners.clients.name')}>{(id) => <input id={id} name="name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label={t('portal.partners.clients.ownerEmail')}>{(id) => <input id={id} name="ownerEmail" type="email" required value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />}</Field>
          <Field label={t('portal.partners.clients.locale')}>
            {(id) => <select id={id} name="locale" value={locale} onChange={(e) => setLocale(e.target.value as 'en' | 'ar')}><option value="en">English</option><option value="ar">العربية</option></select>}
          </Field>
          <Button type="submit" variant="primary" disabled={act.pending || name.trim().length < 2 || !ownerEmail}>{t('portal.partners.clients.addAndInvite')}</Button>
        </form>
      )}
      {act.error && isAllowanceExhausted(act.error) ? (
        <Notice tone="warn" testId="allowance-exhausted">{t('portal.partners.clients.allowance')} <a href="/billing/upgrade">{t('portal.kit.upgrade')}</a></Notice>
      ) : <ErrorNote error={act.error} />}
    </Screen>
  );
}
